import React, { useState } from 'react';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    MenuItem,
    Paper,
    Grid,
    Stack,
    Alert,
    Snackbar
} from '@mui/material';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import SendIcon from '@mui/icons-material/Send';
import { contactService } from '../services/contactService';
import useSEO from '../hooks/useSEO';

const requestTypes = [
    { value: 'consulting', label: 'مشاوره (Consulting)' },
    { value: 'troubleshooting', label: 'عیب‌یابی (Troubleshooting)' },
    { value: 'web_design', label: 'طراحی وب‌سایت (Web Design)' },
    { value: 'security', label: 'امنیت و تست نفوذ (Security)' },
    { value: 'social_media', label: 'شبکه‌های اجتماعی (Social Media)' },
    { value: 'other', label: 'سایر موارد (Other)' }
];

const FreeConsultationPage: React.FC = () => {
    useSEO({
        title: 'دریافت مشاوره IT',
        description: 'درخواست مشاوره رایگان فناوری اطلاعات از تیم چیستا.',
        canonical: '/free-consultation',
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        requestType: '',
        explanation: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        try {
            const result = await contactService.sendConsultationRequest(formData);
            if (result.success) {
                setSubmitted(true);
                setOpenSnackbar(true);
            } else {
                setError(result.message || 'خطایی در ثبت درخواست رخ داد.');
            }
        } catch (err: any) {
            console.error('Submission error:', err);
            setError(err.response?.data?.error || 'ارتباط با سرور برقرار نشد. لطفا دوباره تلاش کنید.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
            {/* Hero Section */}
            <Box 
                sx={{ 
                    bgcolor: '#2854a1', 
                    color: 'white', 
                    py: { xs: 6, md: 8 }, 
                    textAlign: 'center',
                    minHeight: { xs: 150, md: 220 },
                    display: 'flex',
                    alignItems: 'center',
                    mb: 6
                }}
            >
                <Container maxWidth="md">
                    <Typography 
                        variant="h1" 
                        fontWeight={900} 
                        sx={{ 
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                            mb: 2
                        }}
                    >
                        {'\u2067دریافت مشاوره IT\u2069'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, direction: 'rtl' }}>
                        اطلاعات خود را وارد کنید تا متخصصین ما در اسرع وقت با شما تماس بگیرند.
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md">
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 4, md: 6 }, 
                        borderRadius: 6, 
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                    }}
                >
                    {error && (
                        <Alert severity="error" sx={{ mb: 4, direction: 'rtl', borderRadius: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {submitted ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Alert 
                                severity="success" 
                                sx={{ 
                                    mb: 4, 
                                    direction: 'rtl', 
                                    borderRadius: 3,
                                    fontSize: '1.1rem',
                                    '& .MuiAlert-icon': { fontSize: '2rem' }
                                }}
                            >
                                درخواست شما با موفقیت ثبت شد. تیم پشتیبانی چیستا به زودی با شما تماس خواهد گرفت.
                            </Alert>
                            <Button 
                                variant="outlined" 
                                onClick={() => setSubmitted(false)}
                                sx={{ borderRadius: 2, px: 4 }}
                            >
                                ارسال درخواست جدید
                            </Button>
                        </Box>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3} sx={{ direction: 'rtl' }}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        label="نام و نام خانوادگی"
                                        name="name"
                                        placeholder="نام خود را وارد کنید"
                                        value={formData.name}
                                        onChange={handleChange}
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        label="شماره تماس"
                                        name="phone"
                                        placeholder="مثال: ۰۴۰۰۰۰۰۰۰۰"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        label="آدرس ایمیل"
                                        name="email"
                                        type="email"
                                        placeholder="email@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        select
                                        required
                                        fullWidth
                                        label="نوع درخواست"
                                        name="requestType"
                                        value={formData.requestType}
                                        onChange={handleChange}
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    >
                                        {requestTypes.map((option) => (
                                            <MenuItem key={option.value} value={option.value} sx={{ direction: 'rtl' }}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        multiline
                                        rows={5}
                                        label="توضیحات درخواست"
                                        name="explanation"
                                        value={formData.explanation}
                                        onChange={handleChange}
                                        placeholder="لطفاً جزئیات پروژه یا مشکل خود را اینجا بنویسید..."
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            startIcon={<SendIcon sx={{ ml: 1, mr: 0 }} />}
                                            disabled={loading}
                                            sx={{ 
                                                bgcolor: '#2854a1', 
                                                py: 2, 
                                                borderRadius: 3,
                                                fontSize: '1.1rem',
                                                fontWeight: 800,
                                                '&:hover': { bgcolor: '#1e3d7a' }
                                            }}
                                        >
                                            {loading ? 'در حال ارسال...' : 'ارسال درخواست مشاوره'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Paper>

                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: '#2854a1', fontWeight: 700, mb: 3, direction: 'rtl' }}>
                        «هیچ پروژه یا تسکی کوچک نیست، ما مشتاقانه آماده کمک هستیم»
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ color: '#64748b' }}>
                        <ContactSupportIcon />
                        <Typography variant="body2">
                            پشتیبانی ۲۴ ساعته تیم فنی چیستا
                        </Typography>
                    </Stack>
                </Box>
            </Container>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%', direction: 'rtl' }}>
                    پیام شما با موفقیت ارسال شد.
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FreeConsultationPage;
